import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Fade,
} from '@mui/material';
import { styled } from '@mui/system';
import ProjectForm from './ProjectForm';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../constants';


// Styled components for glassmorphism
const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: '#e5e7eb',
  },
  transition: 'background-color 0.3s ease',
});

function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [downloading, setDownloading] = useState(null); // Track downloading project ID
  const navigate = useNavigate();

  // Fetch projects
  useEffect(() => {
    axios
      .get(`${BASE_URL}projects/`)
      .then((response) => setProjects(response.data))
      .catch((error) => console.error('Error fetching projects:', error));
  }, []);

  // Delete project
  const handleDelete = (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      axios
        .delete(`${BASE_URL}projects/${projectId}/`)
        .then(() => {
          setProjects(projects.filter((project) => project.id !== projectId));
        })
        .catch((error) => {
          console.error('Error deleting project:', error);
          alert('Failed to delete project.');
        });
    }
  };

  // Handle form submission
  const handleFormSubmit = (projectData) => {
    if (editProject) {
      axios
        .put(`${BASE_URL}projects/${editProject.id}/`, projectData)
        .then((response) => {
          setProjects(
            projects.map((project) =>
              project.id === editProject.id ? response.data : project
            )
          );
          setEditProject(null);
          setOpenForm(false);
        })
        .catch((error) => {
          console.error('Error updating project:', error);
          alert('Failed to update project.');
        });
    } else {
      axios
        .post(`${BASE_URL}projects/`, projectData)
        .then((response) => {
          setProjects([...projects, response.data]);
          setOpenForm(false);
        })
        .catch((error) => {
          console.error('Error adding project:', error);
          alert('Failed to add project.');
        });
    }
  };

  // Download PDF report
  const handleDownloadReport = (projectId, projectName) => {
    setDownloading(projectId); // Set loading state
    axios
      .get(`${BASE_URL}projects/${projectId}/report/pdf/`, {
        responseType: 'blob',
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `project_${projectName}_report.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error downloading PDF:', error);
        alert('Failed to download PDF report.');
      })
      .finally(() => {
        setDownloading(null); // Clear loading state
      });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        padding: { xs: '16px', md: '32px' },
        color: '#1e293b',
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        {/* Header */}
        <Typography
          variant="h4"
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            color: '#1e40af',
            mb: '24px',
          }}
        >
          Projects
        </Typography>

        {/* Add Project Button */}
        <Button
          variant="contained"
          color="primary"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            padding: '10px 20px',
            mb: '24px',
            '&:hover': {
              transform: 'scale(1.05)',
              transition: 'transform 0.2s ease',
            },
          }}
          onClick={() => setOpenForm(true)}
        >
          Add Project
        </Button>

        {/* Project Form */}
        {openForm && (
          <Fade in={openForm}>
            <Box sx={{ mb: '24px' }}>
              <ProjectForm
                project={editProject}
                onSubmit={handleFormSubmit}
                onCancel={() => {
                  setOpenForm(false);
                  setEditProject(null);
                }}
              />
            </Box>
          </Fade>
        )}

        {/* Projects Table */}
        <GlassPaper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 600 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 600 }}>
                    Area
                  </TableCell>
                  <TableCell sx={{ color: '#1e293b', fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => (
                  <StyledTableRow key={project.id}>
                    <TableCell sx={{ color: '#1e293b' }}>
                      {project.name}
                    </TableCell>
                    <TableCell sx={{ color: '#1e293b' }}>
                      {project.area}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          padding: '6px 12px',
                          margin: '0 4px',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s ease',
                          },
                        }}
                        onClick={() => navigate(`/projects/${project.id}/houses`)}
                      >
                        View Houses
                      </Button>

{/*                       
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          padding: '6px 12px',
                          margin: '0 4px',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s ease',
                          },
                        }}
                        onClick={() => handleDownloadReport(project.id, project.name)}
                        disabled={downloading === project.id}
                        startIcon={
                          downloading === project.id ? (
                            <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                          ) : null
                        }
                      >
                        {downloading === project.id ? 'Generating...' : 'Download Report'}
                      </Button> */}


                      <Button
                        variant="contained"
                        color="warning"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          padding: '6px 12px',
                          margin: '0 4px',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s ease',
                          },
                        }}
                        onClick={() => {
                          setEditProject(project);
                          setOpenForm(true);
                        }}
                      >
                        Edit Project
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          padding: '6px 12px',
                          margin: '0 4px',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            transition: 'transform 0.2s ease',
                          },
                        }}
                        onClick={() => handleDelete(project.id)}
                      >
                        Delete Project
                      </Button>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassPaper>
      </Box>
    </Box>
  );
}

export default ProjectList;